import React, { useState, useEffect, useCallback } from 'react';
import ChatMessageNotification from './ChatMessageNotification';

const NotificationManager = ({ onNotificationClick }) => {
  const [notifications, setNotifications] = useState([]);

  // Function to add a new notification
  const addNotification = useCallback((notification) => {
    const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNotification = {
      id,
      ...notification,
      timestamp: Date.now()
    };
    
    setNotifications(prev => {
      // Limit to 3 notifications max
      const updated = [newNotification, ...prev].slice(0, 3);
      return updated;
    });

    return id;
  }, []);

  // Function to remove a notification
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Function to clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Handle notification click
  const handleNotificationClick = useCallback((notification) => {
    // Call the parent handler
    onNotificationClick?.(notification);
    
    // Remove this notification
    removeNotification(notification.id);
  }, [onNotificationClick, removeNotification]);

  // Make functions available globally
  useEffect(() => {
    window.addChatNotification = addNotification;
    window.clearChatNotifications = clearAllNotifications;
    
    // Listen for global notification events
    const handleGlobalNotification = (event) => {
      const { type, data } = event.detail;
      
      if (type === 'chat-message') {
        addNotification({
          type: 'chat-message',
          message: data.message,
          sender: data.sender,
          channel: data.channel,
          messageData: data.messageData
        });
      }
    };

    window.addEventListener('showNotification', handleGlobalNotification);
    
    return () => {
      window.addChatNotification = null;
      window.clearChatNotifications = null;
      window.removeEventListener('showNotification', handleGlobalNotification);
    };
  }, [addNotification, clearAllNotifications]);

  return (
    <div className="fixed top-16 right-0 z-50 pointer-events-none">
      <div className="p-4 space-y-3">
        {notifications.map((notification, index) => (
          <div
            key={notification.id}
            className="pointer-events-auto"
            style={{
              transform: `translateY(${index * 8}px)`,
              zIndex: 50 - index
            }}
          >
            {notification.type === 'chat-message' && (
              <ChatMessageNotification
                message={notification.message}
                sender={notification.sender}
                onClose={() => removeNotification(notification.id)}
                onClick={() => handleNotificationClick(notification)}
                duration={3000}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationManager;